import React from "react";

import { useLocation, useNavigate, useTranslation, useUserContext } from "utils";

import { COUNTRY_CODE } from "consts";

import { CMC_MIX_TYPE } from "models";

import { Button, Flex, Switch, Tag, Typography } from "components";

import { useCropMixComposerContext } from "pages/app/crop-mix-composer/CropMixComposerContextProvider.tsx";
import { CMC_ROUTES, MAX_SELECTABLE_GOALS } from "pages/app/crop-mix-composer/CropMixComposerOverlay";
import { useActiveCropMixSettings } from "pages/app/crop-mix-composer/utils/useActiveCropMixSettings";

type CropMixComposerFooterProps = {
  step: CMC_ROUTES | null;
  onNextClicked: (mixType: CMC_MIX_TYPE | null) => void;
  onApplyToFieldClicked: () => void;
  onBackClicked: () => void;
  fieldId?: string;
};

export const CropMixComposerFooter = ({ step, onNextClicked, onApplyToFieldClicked, onBackClicked, fieldId }: CropMixComposerFooterProps) => {
  const { t } = useTranslation("cropMixComposer");
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useUserContext();
  const { activeCropMixId, isChangingStep, isPageLoading, isCalculatingPotentialPayout, selectedGoals, autoMixMode, setAutoMixMode } =
    useCropMixComposerContext();
  const activeSettingsCMC = useActiveCropMixSettings();

  const basePath = `${location.pathname.split("cmc")[0].replace(/\/$/, "")}/`;

  const countriesToShowCommercialMix = [COUNTRY_CODE.GERMANY, COUNTRY_CODE.AUSTRIA, COUNTRY_CODE.SWITZERLAND, COUNTRY_CODE.ZULU];
  const showCommercialMix = countriesToShowCommercialMix.some((countryCode) => countryCode === user?.farm.country);

  const mixType = activeSettingsCMC?.mixType || null;

  const isFieldManagementOverview = fieldId && step === CMC_ROUTES.OVERVIEW;

  return (
    <Flex className="crop-mix-composer-overlay footer" data-testid={`crop-mix-composer-overlay-footer-step-${step}`} align="center" justify="space-between">
      <Flex gap="2rem" align="center">
        {!mixType && step !== CMC_ROUTES.OVERVIEW && step !== CMC_ROUTES.SETTINGS ? (
          <Button
            className="back-button"
            dataTestId="crop-mix-composer-overlay-footer-back-button"
            size="middle"
            type="text"
            onClick={onBackClicked}
            disabled={isChangingStep !== null || isPageLoading}
            loading={isChangingStep === "previous"}
          >
            {t("back")}
          </Button>
        ) : (
          <span />
        )}
      </Flex>
      <Flex gap="2rem" align="center">
        {step === CMC_ROUTES.GOALS && !mixType ? (
          <Flex align="center" gap="0.5rem">
            <Typography variant="paragraph-m" className="margin-0 auto-mix-mode-toggle-text no-wrap">
              {t(`page.${CMC_ROUTES.GOALS}.footer.autoMixModeToggle`)}
            </Typography>
            <Switch
              className="cmc-auto-mix-mode-toggle"
              data-testid="cmc-auto-mix-mode-toggle"
              checked={autoMixMode}
              disabled={isChangingStep !== null || isPageLoading}
              onChange={() => setAutoMixMode(!autoMixMode)}
            />
            <Tag
              colour="blue"
              text={
                <Typography variant="paragraph-s" bold className="margin-0 no-wrap">
                  {t(`page.${CMC_ROUTES.GOALS}.footer.betaTag`)}
                </Typography>
              }
              className="margin-0"
            />
          </Flex>
        ) : step === CMC_ROUTES.OVERVIEW && showCommercialMix ? (
          <Button
            size="middle"
            type="text"
            disabled={isChangingStep !== null || isPageLoading}
            onClick={() => navigate(`${basePath}cmc/${activeCropMixId}/com-mixes${location.search}`)}
          >
            {t(`page.${CMC_ROUTES.OVERVIEW}.commercialMix`)}
          </Button>
        ) : null}
        {step !== CMC_ROUTES.OVERVIEW || isFieldManagementOverview ? (
          <Button
            className={isFieldManagementOverview ? "apply-to-field-button" : "next-button"}
            dataTestId={isFieldManagementOverview ? "crop-mix-composer-overlay-footer-apply-to-field-button" : "crop-mix-composer-overlay-footer-next-button"}
            size="middle"
            type="primary"
            onClick={isFieldManagementOverview ? onApplyToFieldClicked : () => onNextClicked(mixType)}
            disabled={isChangingStep !== null || isPageLoading || (isCalculatingPotentialPayout && step === CMC_ROUTES.OVERVIEW)}
            loading={isChangingStep === "next" || (isCalculatingPotentialPayout && step === CMC_ROUTES.OVERVIEW)}
          >
            {/* we don't show the "continue" button in the overview step of the cmc standalone flow
                if we have a mix type, it means that we are in the edit mode for the current step, so we show the "save changes" button
                if we don't have a mix type, it means that we are in the create mode for the current step, so we show the regular "continue" button text */}
            {step !== CMC_ROUTES.OVERVIEW && mixType
              ? t("saveChanges")
              : t(`page.${step}.footer.continueButton`, { selectedGoalsCount: selectedGoals.length, maxSelectableGoals: MAX_SELECTABLE_GOALS })}
          </Button>
        ) : null}
      </Flex>
    </Flex>
  );
};
